import { computed, ref } from 'vue'
import { orderBy } from 'lodash'
import { defineStore } from 'pinia'

import { AnalyticsIrrMoic } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '@/const'

import { DuckDBService } from '@/services/duckdb'

import { getCurrentDate } from '@/helpers/dates'
import { getJoinedFilterValues } from '../utils/helpers'

import useAnalyticsStore from '../index'

export const TABLE_NAME = 'analytics_irrmoic'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  [AnalyticsField.IRR]: 'DOUBLE',
  [AnalyticsField.MOIC]: 'DOUBLE',
}

export default defineStore('analytics-irr_moic', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  const datesRange = ref<string[]>([])

  // GETTERS
  const isInit = computed(() => deprecated.value !== undefined)

  const getDatesRange = computed(() => datesRange.value)

  // SETTERS
  const setList = async (data: {
    irr_moic_view: AnalyticsIrrMoic[];
  }) => {
    if (deprecated.value === false) return
    if (!data.irr_moic_view) return
    deprecated.value = false
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await duckdbInstance.pushData(TABLE_NAME, data.irr_moic_view, TABLE_SCHEMA)
    trigger.value++
  }

  // ACTIONS

  const clear = () => {
    datesRange.value = []
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getDatesRange,

    setList,

    clear,
  }
})

export const useAnalyticsIrrMoicFilters = () => {

  const analyticsStore = useAnalyticsStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getJoinedFilterValues(TABLE_NAME, analyticsDate)

  const filters = computed(() => {
    const result: FilterField[] = [
      {
        name: AnalyticsField.ACCOUNT,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID)
      },
      {
        name: AnalyticsField.ASSET,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
        getter: () => getValues(AnalyticsField.ASSET_ID)
      },
      {
        name: AnalyticsField.IRR,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.IRR],
        isAmount: true,
      },
      {
        name: AnalyticsField.MOIC,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.MOIC],
        isAmount: true,
      },
    ]
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
